import React, { useState, useEffect } from "react";
import { Save, CreditCard, Info, Building2, ArrowRightLeft, CheckCircle2, Landmark } from "lucide-react";
import { Spinner } from "react-bootstrap";
import { AppButton, AppCard, AppCardHeader, AppCardTitle, AppCardContent } from "../ui";
import apiClient from "../../utils/apiClient";
import "./BankDetailsSettings.scss";

const BankDetailsSettings = () => {
  const [institute, setInstitute] = useState(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState("own_gateway");
  const [bankDetails, setBankDetails] = useState({
    accountHolderName: "",
    accountNumber: "",
    ifscCode: "",
    bankName: "",
    upiId: "",
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/institute/settings");
        const inst = data.institute;
        setInstitute(inst);
        setSelectedPaymentType(inst?.paymentType || "own_gateway");
        if (inst?.bankDetails) {
          setBankDetails((prev) => ({ ...prev, ...inst.bankDetails }));
        }
      } catch {
        // defaults
      }
    })();
  }, []);

  const update = (key) => (e) => {
    const value = key === 'ifscCode' ? e.target.value.toUpperCase() : e.target.value;
    setBankDetails((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validateBankDetails = () => {
    const errors = {};
    const bd = bankDetails;
    const hasBankPair = bd.accountNumber && bd.ifscCode;
    const hasUpi = !!bd.upiId;

    if (!hasBankPair && !hasUpi) {
      errors._form = 'Provide either bank account + IFSC OR a UPI ID.';
      return errors;
    }
    if (bd.ifscCode && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bd.ifscCode)) {
      errors.ifscCode = 'Invalid IFSC (4 letters + 0 + 6 alphanumeric, e.g. SBIN0001234).';
    }
    if (bd.accountNumber && !/^\d{9,18}$/.test(bd.accountNumber)) {
      errors.accountNumber = 'Account number must be 9-18 digits.';
    }
    if (bd.upiId && !/^[a-zA-Z0-9._-]{2,256}@[a-zA-Z]{2,64}$/.test(bd.upiId)) {
      errors.upiId = 'UPI ID must be in name@handle format.';
    }
    if (bd.accountNumber && !bd.accountHolderName) {
      errors.accountHolderName = 'Account holder name is required.';
    }
    return errors;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMsg(null);
      const payload = { paymentType: selectedPaymentType };
      if (selectedPaymentType === "lms_payment") {
        const errors = validateBankDetails();
        if (Object.keys(errors).length > 0) {
          setFieldErrors(errors);
          setMsg({ type: "error", text: errors._form || 'Please fix the highlighted fields.' });
          setSaving(false);
          return;
        }
        setFieldErrors({});
        payload.bankDetails = bankDetails;
      }

      await apiClient.put("/institute/settings", payload);
      setInstitute((prev) => ({ ...prev, paymentType: selectedPaymentType }));
      setMsg({ type: "success", text: "Payment settings saved!" });
    } catch (err) {
      setMsg({ type: "error", text: err?.response?.data?.message || "Failed to save." });
    } finally {
      setSaving(false);
    }
  };

  if (!institute) return null;

  const paymentType = selectedPaymentType || institute.paymentType || "own_gateway";

  return (
    <div className="pmt-config">
      {/* ── Payment Type Selector — radio cards ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="pmt-card-title"><ArrowRightLeft size={18} /> Payment Mode</span>
          </AppCardTitle>
          <p className="pmt-card-desc">Choose how fee payments are collected and processed</p>
        </AppCardHeader>
        <AppCardContent>
          <div className="pmt-type-grid">
            {/* Own Razorpay */}
            <button
              type="button"
              className={`pmt-type-card ${paymentType === "own_gateway" ? "pmt-type-card--active" : ""}`}
              onClick={() => setSelectedPaymentType("own_gateway")}
            >
              <div className="pmt-type-card__radio">
                {paymentType === "own_gateway" && <CheckCircle2 size={18} />}
              </div>
              <div className="pmt-type-card__icon pmt-type-card__icon--emerald">
                <CreditCard size={22} />
              </div>
              <div className="pmt-type-card__content">
                <h4>Own Razorpay</h4>
                <p>Use your own Razorpay keys. Payments go directly to your account.</p>
              </div>
            </button>

            {/* LMS Payouts */}
            <button
              type="button"
              className={`pmt-type-card ${paymentType === "lms_payment" ? "pmt-type-card--active" : ""}`}
              onClick={() => setSelectedPaymentType("lms_payment")}
            >
              <div className="pmt-type-card__radio">
                {paymentType === "lms_payment" && <CheckCircle2 size={18} />}
              </div>
              <div className="pmt-type-card__icon pmt-type-card__icon--blue">
                <Building2 size={22} />
              </div>
              <div className="pmt-type-card__content">
                <h4>LMS Payouts</h4>
                <p>Platform collects payments. Earnings are settled to your bank after commission.</p>
              </div>
            </button>
          </div>

          {/* Info banner */}
          <div className={`pmt-info-banner pmt-info-banner--${paymentType === "lms_payment" ? "blue" : "emerald"}`}>
            <Info size={15} />
            <span>
              {paymentType === "lms_payment"
                ? "Fee payments are collected via the platform gateway. A commission is deducted and the remainder is settled to your bank."
                : "You are using your own Razorpay gateway. All payments are collected directly into your Razorpay account."}
            </span>
          </div>

          {/* Pending payout notice */}
          {institute.paymentType === "lms_payment" && paymentType === "own_gateway" && (
            <div className="pmt-info-banner pmt-info-banner--amber">
              <Info size={15} />
              <span>Switching from LMS Payouts to Own Razorpay is blocked while pending payouts exist.</span>
            </div>
          )}

          <div className="pmt-save-row">
            <AppButton onClick={handleSave} disabled={saving} size="sm">
              {saving ? <><Spinner size="sm" className="me-1" /> Saving...</>
                : <><Save size={14} className="me-1" /> Save Payment Mode</>}
            </AppButton>
            {msg && (
              <span className={`pmt-save-msg pmt-save-msg--${msg.type}`}>{msg.text}</span>
            )}
          </div>
        </AppCardContent>
      </AppCard>

      {/* ── Bank Details — only for LMS Payouts ── */}
      {paymentType === "lms_payment" && (
        <AppCard>
          <AppCardHeader>
            <AppCardTitle>
              <span className="pmt-card-title"><Landmark size={18} /> Bank Details for Settlements</span>
            </AppCardTitle>
            <p className="pmt-card-desc">Provide your bank account so the platform can settle your earnings</p>
          </AppCardHeader>
          <AppCardContent>
            <div className="pmt-bank-grid">
              <div className="pmt-field">
                <label>Account Holder Name</label>
                <input
                  type="text"
                  className={`pmt-input${fieldErrors.accountHolderName ? ' pmt-input--error' : ''}`}
                  value={bankDetails.accountHolderName}
                  onChange={update("accountHolderName")}
                  placeholder="e.g. John Doe"
                />
                {fieldErrors.accountHolderName && <small className="pmt-field__error">{fieldErrors.accountHolderName}</small>}
              </div>
              <div className="pmt-field">
                <label>Account Number</label>
                <input
                  type="text"
                  className={`pmt-input${fieldErrors.accountNumber ? ' pmt-input--error' : ''}`}
                  value={bankDetails.accountNumber}
                  onChange={update("accountNumber")}
                  placeholder="e.g. 1234567890"
                  inputMode="numeric"
                />
                {fieldErrors.accountNumber && <small className="pmt-field__error">{fieldErrors.accountNumber}</small>}
              </div>
              <div className="pmt-field">
                <label>IFSC Code</label>
                <input
                  type="text"
                  className={`pmt-input${fieldErrors.ifscCode ? ' pmt-input--error' : ''}`}
                  value={bankDetails.ifscCode}
                  onChange={update("ifscCode")}
                  placeholder="e.g. SBIN0001234"
                  maxLength={11}
                />
                {fieldErrors.ifscCode && <small className="pmt-field__error">{fieldErrors.ifscCode}</small>}
              </div>
              <div className="pmt-field">
                <label>Bank Name</label>
                <input
                  type="text"
                  className="pmt-input"
                  value={bankDetails.bankName}
                  onChange={update("bankName")}
                  placeholder="e.g. State Bank of India"
                />
              </div>
              <div className="pmt-field pmt-field--full">
                <label>UPI ID <span className="pmt-field__optional">(optional if bank account provided)</span></label>
                <input
                  type="text"
                  className={`pmt-input${fieldErrors.upiId ? ' pmt-input--error' : ''}`}
                  value={bankDetails.upiId}
                  onChange={update("upiId")}
                  placeholder="e.g. name@upi"
                />
                {fieldErrors.upiId && <small className="pmt-field__error">{fieldErrors.upiId}</small>}
              </div>
            </div>

            <div className="pmt-save-row">
              <AppButton onClick={handleSave} disabled={saving} size="sm">
                {saving ? <><Spinner size="sm" className="me-1" /> Saving...</>
                  : <><Save size={14} className="me-1" /> Save Bank Details</>}
              </AppButton>
              {msg && (
                <span className={`pmt-save-msg pmt-save-msg--${msg.type}`}>{msg.text}</span>
              )}
            </div>
          </AppCardContent>
        </AppCard>
      )}
    </div>
  );
};

export default BankDetailsSettings;
